import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "../lib/utils"

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg text-body-md font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-6 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Solid variants
        primary:
          "bg-primary text-bg-white hover:bg-primary/90 disabled:bg-disabled disabled:text-bg-white",
        secondary:
          "bg-secondary text-bg-white hover:bg-secondary/90 disabled:bg-disabled disabled:text-bg-white",
        neutral:
          "bg-background text-text hover:bg-background/80 disabled:bg-disabled disabled:text-text-disabled",
        // Outlined variants
        "outline-primary":
          "border border-primary bg-bg-white text-primary hover:bg-bg-primary disabled:border-disabled disabled:text-text-disabled",
        "outline-secondary":
          "border border-secondary bg-bg-white text-secondary hover:bg-bg-secondary disabled:border-disabled disabled:text-text-disabled",
        "outline-neutral":
          "border border-border bg-bg-white text-text hover:bg-background disabled:border-disabled disabled:text-text-disabled",
        // Legacy shadcn variants for backward compatibility
        default: "bg-primary text-bg-white hover:bg-primary/90",
        destructive: "bg-attention text-bg-white hover:bg-attention/90",
        outline: "border border-border bg-bg-white text-text hover:bg-background",
        ghost: "hover:bg-background hover:text-text",
        link: "text-primary underline-offset-4 hover:underline",
        // Social login button variants
        google: "border border-border bg-bg-white text-text hover:bg-background",
        facebook: "bg-[#1877F2] text-bg-white hover:bg-[#1877F2]/90",
        line: "bg-[#00B900] text-bg-white hover:bg-[#00B900]/90",
        x: "bg-black text-bg-white hover:bg-black/90",
        apple: "bg-black text-bg-white hover:bg-black/90",
        instagram:
          "bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] text-bg-white hover:opacity-90",
      },
      size: {
        default: "h-11 min-h-9 px-4",
        sm: "h-9 px-3 text-body-sm",
        lg: "h-12 px-6 text-body-lg",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
